import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { EditOfferDialog } from './EditOfferDialog';
import { DeleteOfferDialog } from './DeleteOfferDialog';
import { getPartnerOffers } from '@/lib/firebase/queries/offers';
import { toast } from 'sonner';
import type { PartnerOffer } from './types';

interface OfferListProps {
  searchQuery: string;
}

export function OfferList({ searchQuery }: OfferListProps) {
  const [offers, setOffers] = useState<PartnerOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<PartnerOffer | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { t, dir } = useLanguage();

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const partnerOffers = await getPartnerOffers();
      setOffers(partnerOffers);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error(t('dashboard.partner.dashboard.offers.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const filteredOffers = offers.filter(offer =>
    offer.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    offer.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        {t('dashboard.partner.dashboard.offers.noOffers')}
      </div>
    );
  }

  if (filteredOffers.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        {t('dashboard.partner.dashboard.offers.noResults')}
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.title')}
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.description')}
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.points')}
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.discount')}
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.validUntil')}
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.status')}
              </TableHead>
              <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                {t('dashboard.partner.dashboard.offers.table.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOffers.map((offer) => (
              <TableRow key={offer.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {offer.imageUrl && (
                      <img
                        src={offer.imageUrl}
                        alt={offer.title}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    )}
                    {offer.title}
                  </div>
                </TableCell>
                <TableCell>{offer.description}</TableCell>
                <TableCell>{offer.pointsRequired}</TableCell>
                <TableCell>{offer.discountPercentage}%</TableCell>
                <TableCell>{format(offer.validUntil, 'PPP')}</TableCell>
                <TableCell>
                  <Badge variant={
                    offer.status === 'active' ? 'default' :
                    offer.status === 'inactive' ? 'secondary' :
                    'destructive'
                  }>
                    {t(`dashboard.partner.dashboard.offers.status.${offer.status}`)}
                  </Badge>
                </TableCell>
                <TableCell className={dir === 'rtl' ? 'text-left' : 'text-right'}>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedOffer(offer);
                        setShowEditDialog(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedOffer(offer);
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EditOfferDialog
        offer={selectedOffer}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSuccess={fetchOffers}
      />

      <DeleteOfferDialog
        offer={selectedOffer}
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onSuccess={fetchOffers}
      />
    </>
  );
} 